import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { router } from "@inertiajs/react"
import { IconTrash } from "@tabler/icons-react"
import { useState } from "react"
import type { DocumentDetail, ShareableUser } from "../types"

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  document: DocumentDetail
  shareableUsers: ShareableUser[]
}

export function ShareDialog({ open, onOpenChange, document: doc, shareableUsers }: ShareDialogProps) {
  const [userId, setUserId] = useState("")
  const [access, setAccess] = useState("viewer")

  const reload = { preserveScroll: true, only: ["document", "shareable_users", "tree"] }

  const addShare = () => {
    if (!userId) return

    router.post(
      `/documents/${doc.id}/shares`,
      { document_share: { user_id: Number(userId), access } },
      {
        ...reload,
        onSuccess: () => {
          setUserId("")
          setAccess("viewer")
        },
      },
    )
  }

  const saveSharing = (visibility: string, defaultAccess: string) =>
    router.patch(`/documents/${doc.id}/sharing`, { document: { visibility, default_access: defaultAccess } }, reload)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share</DialogTitle>
          <DialogDescription>
            {doc.is_root
              ? "Everyone you add can see this page and every page nested under it."
              : `Access is set on ${doc.share_target.title}, the top of this tree, and applies to every page beneath it.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="visibility">Who can find it</Label>
          <Select value={doc.visibility} onValueChange={(value) => saveSharing(value, doc.default_access)}>
            <SelectTrigger id="visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Private — only people you add</SelectItem>
              <SelectItem value="company">Company — everyone at the company</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {doc.visibility === "company" && (
          <div className="space-y-2">
            <Label htmlFor="default_access">What the company can do</Label>
            <Select value={doc.default_access} onValueChange={(value) => saveSharing(doc.visibility, value)}>
              <SelectTrigger id="default_access">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">Read it</SelectItem>
                <SelectItem value="edit">Read and edit it</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <Separator />

        <div className="space-y-3">
          {doc.shares.length === 0 && <p className="text-sm text-muted-foreground">Not shared with anyone yet.</p>}

          {doc.shares.map((share) => (
            <div key={share.id} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{share.name}</p>
                <p className="truncate text-xs text-muted-foreground">{share.email}</p>
              </div>

              <Select
                value={share.access}
                onValueChange={(value) =>
                  router.patch(`/documents/${doc.id}/shares/${share.id}`, { document_share: { access: value } }, reload)
                }
              >
                <SelectTrigger className="w-32" aria-label={`Access for ${share.name}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Can view</SelectItem>
                  <SelectItem value="editor">Can edit</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${share.name}`}
                onClick={() => router.delete(`/documents/${doc.id}/shares/${share.id}`, reload)}
              >
                <IconTrash className="size-4" />
              </Button>
            </div>
          ))}
        </div>

        <Separator />

        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1 space-y-2">
            <Label htmlFor="share_user">Add someone</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger id="share_user">
                <SelectValue placeholder="Pick a person" />
              </SelectTrigger>
              <SelectContent>
                {shareableUsers.map((user) => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Select value={access} onValueChange={setAccess}>
            <SelectTrigger className="w-32" aria-label="Access for the new person">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="viewer">Can view</SelectItem>
              <SelectItem value="editor">Can edit</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={addShare} disabled={!userId}>
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
